import {
  CellId,
  GridCellElement,
  GridElement,
  GridElementType,
  GridLayoutId,
  GridRow,
  GridRowId,
  GridSharedFilterElement,
  SharedFilterId,
  assertNever,
} from "@hex/common";

import {
  GridElementMP,
  GridRowMP,
} from "../../../../redux/slices/hexVersionMPSlice.js";

export type GridRowsChange = {
  gridLayoutId: GridLayoutId;
  gridRows: readonly GridRow[];
  createdGridRowIds: ReadonlySet<GridRowId>;
  modifiedGridRowIds: ReadonlySet<GridRowId>;
  removedGridRowIds: ReadonlySet<GridRowId>;
};

export type PartialGridRowsChange = Partial<GridRowsChange> &
  Pick<GridRowsChange, "gridRows" | "gridLayoutId">;

/**
 * Compares two grid layouts to determine if they're the same layout shape or not.
 * Useful for figuring out if a user has modified things compared to an auto-layout or not
 */
export const isSameLayout = (
  rows1: readonly (GridRow | GridRowMP)[],
  rows2: readonly (GridRow | GridRowMP)[],
): boolean => {
  const resolvedRows1 = rows1.map(maybeGridRowMPToGridRow);
  const resolvedRows2 = rows2.map(maybeGridRowMPToGridRow);

  if (resolvedRows1.length !== resolvedRows2.length) return false;
  return resolvedRows1.every((row1, i) => {
    const row2 = resolvedRows2[i];
    if (row1.gridLayoutId !== row2.gridLayoutId) return false;
    if (row1.gridColumns.length !== row2.gridColumns.length) return false;

    return row1.gridColumns.every((col1, j) => {
      const col2 = row2.gridColumns[j];
      if (col1.gridElements.length !== col2.gridElements.length) return false;
      if (col1.start !== col2.start || col1.end !== col2.end) return false;

      return col1.gridElements.every((ele1, k) => {
        const ele2 = col2.gridElements[k];
        return (
          ele1.entityId === ele2.entityId &&
          (ele1.height === ele2.height ||
            (ele1.height == null && ele2.height == null)) &&
          ele1.type === ele2.type
        );
      });
    });
  });
};

function maybeGridRowMPToGridRow(gridRow: GridRowMP | GridRow): GridRow {
  const firstElement = gridRow.gridColumns[0]?.gridElements[0];
  if (firstElement != null && "createdDate" in firstElement) {
    return gridRowMPToGridRow(gridRow as GridRowMP);
  }
  return gridRow as GridRow;
}

export function gridRowMPToGridRow(gridRow: GridRowMP): GridRow {
  return {
    ...gridRow,
    gridColumns: gridRow.gridColumns.map((gridColumn) => ({
      ...gridColumn,
      gridElements: gridColumn.gridElements.map((gridElement) => {
        return visitGridElementMP<GridElement>(gridElement, {
          cell: (element, cellId) => ({
            ...element,
            entityId: cellId,
            type: GridElementType.CELL,
          }),
          sharedFilter: (element, sharedFilterId) => ({
            ...element,
            entityId: sharedFilterId,
            type: GridElementType.SHARED_FILTER,
          }),
        });
      }),
    })),
  };
}

export function visitGridElementMP<T>(
  element: GridElementMP,
  visitor: {
    cell: (element: GridElementMP, cellId: CellId) => T;
    sharedFilter: (element: GridElementMP, sharedFilterId: SharedFilterId) => T;
  },
): T {
  if (element.type === GridElementType.CELL) {
    return visitor.cell(element, element.entityId as CellId);
  } else if (element.type === GridElementType.SHARED_FILTER) {
    return visitor.sharedFilter(element, element.entityId as SharedFilterId);
  } else {
    assertNever(element.type, element.type);
  }
}

export function visitGridElement<T>(
  element: GridElement,
  visitor: {
    cell: (element: GridCellElement) => T;
    sharedFilter: (element: GridSharedFilterElement) => T;
  },
): T {
  if (element.type === GridElementType.CELL) {
    return visitor.cell(element);
  } else if (element.type === GridElementType.SHARED_FILTER) {
    return visitor.sharedFilter(element);
  } else {
    assertNever(element, element);
  }
}

export function visitGridElementType<T>(
  type: GridElementType,
  visitor: {
    cell: () => T;
    sharedFilter: () => T;
  },
): T {
  if (type === GridElementType.CELL) {
    return visitor.cell();
  } else if (type === GridElementType.SHARED_FILTER) {
    return visitor.sharedFilter();
  } else {
    assertNever(type, type);
  }
}

export function filterGridCellElements(
  gridElements: readonly GridElement[],
): GridCellElement[] {
  return gridElements.filter(filterGridCellElement);
}

export function filterGridCellElement(
  gridElement: GridElement,
): gridElement is GridCellElement {
  return gridElement.type === GridElementType.CELL;
}
